/**
 * LogSession Monitor v3 - Main Application
 *
 * Features:
 * 1. Real-time session monitoring via WebSocket
 * 2. Ambient sound notifications (threshold pool)
 * 3. Session mute/unmute per session
 * 4. Drag-and-drop card reordering (monitored grid only)
 * 5. Collapsible All Sessions list
 */

import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Container,
  Title,
  Group,
  Text,
  Badge,
  ActionIcon,
  Slider,
  Stack,
  Button,
  Popover,
  Paper,
  Code,
  SegmentedControl,
  Switch
} from '@mantine/core'
import {
  IconRobot,
  IconSettings,
  IconVolume,
  IconPlayerStop,
  IconLock
} from '@tabler/icons-react'
import { useAuth, LoginButton } from '@session-tools/auth'

import { useSessionMonitor } from './hooks/useSessionMonitor'
import { useSoundManager } from './hooks/useSoundManager'
import { useSessionOrder } from './hooks/useSessionOrder'
import { SessionList } from './components/SessionList'
import { MonitoredGrid } from './components/MonitoredGrid'

// Check if running in dev mode (localhost)
const isDevMode = (): boolean => {
  const hostname = window.location.hostname
  return hostname === 'localhost' || hostname === '127.0.0.1'
}

const isCloudflarePages = (): boolean => {
  const hostname = window.location.hostname
  return hostname.endsWith('.pages.dev') || hostname.endsWith('.microwiseai.com')
}

// Default port for local WebSocket server
const DEFAULT_LOCAL_PORT = 41820

// Get WebSocket URL from localStorage, query parameter, or environment variable
const getWebSocketUrl = (): string | null => {
  // 1. Query parameter: ?ws=wss://example.com (highest priority, saves to localStorage and redirects)
  const params = new URLSearchParams(window.location.search)
  const wsParam = params.get('ws')
  if (wsParam) {
    localStorage.setItem('monitor-ws-url', wsParam)
    // Redirect to clean URL (removes query params)
    window.history.replaceState({}, '', window.location.pathname)
    return wsParam
  }

  // 2. localStorage (saved from previous visit)
  const savedUrl = localStorage.getItem('monitor-ws-url')
  if (savedUrl) return savedUrl

  // 3. Environment variable (build time)
  const envUrl = import.meta.env.VITE_WS_URL
  if (envUrl) return envUrl

  // 4. Cloudflare Pages: auto-connect to localhost with default port
  if (isCloudflarePages()) {
    return `ws://localhost:${DEFAULT_LOCAL_PORT}`
  }

  // 5. Dev mode or local: connect to same host/port
  if (isDevMode()) {
    return `ws://${window.location.host}`
  }

  // 6. Production: connect to same host
  return `ws://${window.location.host}`
}

// Clear saved WebSocket URL
export const clearSavedWebSocketUrl = () => {
  localStorage.removeItem('monitor-ws-url')
  window.location.href = window.location.pathname
}

// Load monitored sessions from localStorage
const loadMonitoredSessions = (): string[] => {
  try {
    const saved = localStorage.getItem('logsession-monitor-monitored')
    return saved ? JSON.parse(saved) : []
  } catch {
    return []
  }
}

export default function App() {
  const [showSoundControls, setShowSoundControls] = useState(false)
  const [monitoredSessions, setMonitoredSessions] = useState<string[]>(loadMonitoredSessions)
  const [wsUrl] = useState<string | null>(getWebSocketUrl)
  const [autoConnectFailed, setAutoConnectFailed] = useState(false)

  const { isLoggedIn } = useAuth()

  // Only connect when we have a URL
  const { sessions, connected, error, lastChangeTimeMap } = useSessionMonitor({
    serverUrl: wsUrl || undefined
  })

  // Auto-connect failure detection
  // Show setup UI when connection to localhost fails (no manual URL configured)
  useEffect(() => {
    const savedUrl = localStorage.getItem('monitor-ws-url')
    const isAutoConnect = !savedUrl && wsUrl === `ws://localhost:${DEFAULT_LOCAL_PORT}`

    if (isAutoConnect && error && !connected) {
      // Give it a moment to retry, then fallback to setup UI
      const timeout = setTimeout(() => {
        setAutoConnectFailed(true)
      }, 3000) // Wait 3 seconds before showing setup UI

      return () => clearTimeout(timeout)
    }

    // Reset if successfully connected
    if (connected) {
      setAutoConnectFailed(false)
    }
  }, [error, connected, wsUrl])

  // Sound manager - must be called before any early returns (React hooks rule)
  const {
    config,
    globalConfig,
    updateConfig,
    updateGlobalConfig,
    isPlaying,
    poolSessions,
    mutedSessions,
    emergencyStop,
    muteSession,
    unmuteSession
  } = useSoundManager(sessions, monitoredSessions)

  // Disable sound completely when not logged in
  useEffect(() => {
    updateGlobalConfig({ masterEnabled: isLoggedIn })
  }, [isLoggedIn, updateGlobalConfig])

  // Show setup UI when no URL configured or auto-connect failed
  const showSetupUI = !wsUrl || autoConnectFailed

  // Save monitored sessions to localStorage
  useEffect(() => {
    localStorage.setItem('logsession-monitor-monitored', JSON.stringify(monitoredSessions))
  }, [monitoredSessions])

  // Add session to monitored list
  const addToMonitored = (name: string) => {
    setMonitoredSessions(prev => prev.includes(name) ? prev : [...prev, name])
  }

  // Remove session from monitored list
  const removeFromMonitored = (name: string) => {
    setMonitoredSessions(prev => prev.filter(s => s !== name))
  }

  // Get monitored sessions as SessionInfo array (for ordering)
  const monitoredSessionInfos = useMemo(() =>
    sessions.filter(s => monitoredSessions.includes(s.name)),
    [sessions, monitoredSessions]
  )

  // Drag-and-drop order management (only for monitored sessions)
  const { orderedSessions: orderedMonitoredSessions, reorder } = useSessionOrder({
    sessions: monitoredSessionInfos,
    storageKey: 'logsession-monitor-monitored-order'
  })

  const handleToggleMute = (sessionName: string) => {
    if (mutedSessions.includes(sessionName)) {
      unmuteSession(sessionName)
    } else {
      muteSession(sessionName)
    }
  }

  // Connection badge
  const getConnectionBadge = () => {
    if (error) {
      return <Badge size="lg" variant="light" color="red">Error</Badge>
    }
    if (connected) {
      return <Badge size="lg" variant="light" color="green">Connected</Badge>
    }
    return <Badge size="lg" variant="light" color="yellow">Reconnecting...</Badge>
  }

  // Global status counts (all sessions)
  const statusCounts = {
    idle: sessions.filter(s => s.status === 'idle').length,
    active: sessions.filter(s => s.status === 'active').length,
    waiting: sessions.filter(s => s.status === 'waiting').length,
    stuck: sessions.filter(s => s.status === 'stuck').length,
    dead: sessions.filter(s => s.status === 'dead').length,
  }

  // PWA Badge API
  useEffect(() => {
    const badgeCount = statusCounts.waiting + statusCounts.idle
    if ('setAppBadge' in navigator) {
      if (badgeCount > 0) {
        (navigator as any).setAppBadge(badgeCount).catch((err: Error) => {
          console.log('Badge API failed:', err)
        })
      } else {
        (navigator as any).clearAppBadge().catch((err: Error) => {
          console.log('Badge API failed:', err)
        })
      }
    }
  }, [statusCounts.waiting, statusCounts.idle])

  // Setup UI - shown when server not connected
  if (showSetupUI) {
    return (
      <Container size="sm" py="xl">
        <Paper shadow="md" p="xl" radius="md">
          <Stack gap="lg">
            <Group>
              <IconRobot size={36} stroke={1.5} />
              <Title order={2}>LogSession Monitor</Title>
            </Group>

            <Text c="dimmed">
              {autoConnectFailed
                ? 'Failed to connect to local server. Install and start the monitor server:'
                : 'Install and start the monitor server to begin:'}
            </Text>

            {/* Installation Steps */}
            <Stack gap="xs">
              <Text size="sm" fw={500}>1. Install tools (one-time setup)</Text>
              <Code block>
{`npm install -g @glpkg/installer
gitlab-install add -g @session-tools/loggable-session@2.1.1
gitlab-install add -g @session-tools/logsession-monitor@1.0.8`}
              </Code>

              <Text size="sm" fw={500} mt="sm">2. Start a session</Text>
              <Code block>logsession start my-session</Code>

              <Text size="sm" fw={500} mt="sm">3. Start the monitor server</Text>
              <Code block>logsession-monitor</Code>
            </Stack>

            <Text size="xs" c="dimmed">
              This page will auto-connect when the server is running on localhost:41820.
              <br />
              <a href="https://docs.glpkg.microwiseai.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                gitlab-install docs
              </a>
            </Text>
          </Stack>
        </Paper>
      </Container>
    )
  }

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" mb="xl">
        <Group>
          <IconRobot size={36} stroke={1.5} />
          <Title order={1}>LogSession Monitor</Title>
        </Group>
        <Group gap="xs">
          {/* Login Button */}
          <LoginButton compact />

          {/* Sound Settings Popover */}
          <Popover
            opened={showSoundControls}
            onChange={setShowSoundControls}
            position="bottom-end"
            width={360}
            shadow="md"
          >
            <Popover.Target>
              <ActionIcon
                variant="default"
                size="lg"
                onClick={() => setShowSoundControls(!showSoundControls)}
                title="Sound Settings"
              >
                <IconSettings size={20} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              {isLoggedIn ? (
                <Stack gap="md">
                  <Group justify="space-between">
                    <Group gap="xs">
                      <IconVolume size={20} />
                      <Text fw={500}>Sound Controls</Text>
                    </Group>
                    {isPlaying && (
                      <Badge size="sm" variant="filled" color="green">
                        Playing
                      </Badge>
                    )}
                  </Group>

                  {/* Volume */}
                  <div>
                    <Text size="sm" mb="xs">Volume: {Math.round(config.volume * 100)}%</Text>
                    <Slider
                      value={config.volume * 100}
                      onChange={(value) => updateConfig({ volume: value / 100 })}
                      min={0}
                      max={100}
                      step={5}
                      label={(value) => `${value}%`}
                    />
                  </div>

                  {/* Wait Threshold */}
                  <div>
                    <Text size="sm" mb="xs">Wait Threshold: {config.waitThresholdSeconds}s</Text>
                    <Slider
                      value={config.waitThresholdSeconds}
                      onChange={(value) => updateConfig({ waitThresholdSeconds: value })}
                      min={5}
                      max={120}
                      step={5}
                      label={(value) => `${value}s`}
                    />
                  </div>

                  {/* Stop Mode */}
                  <div>
                    <Text size="sm" mb="xs">Play Condition</Text>
                    <SegmentedControl
                      fullWidth
                      size="xs"
                      value={globalConfig.stopMode}
                      onChange={(value) => updateGlobalConfig({ stopMode: value as 'all' | 'any' })}
                      data={[
                        { label: 'Waiting only', value: 'all' },
                        { label: 'All sessions', value: 'any' }
                      ]}
                    />
                    <Text size="xs" c="dimmed" mt={4}>
                      {globalConfig.stopMode === 'all'
                        ? 'Pool-based: plays when waiting'
                        : 'Stops when any session is active'}
                    </Text>
                  </div>

                  {/* Status Change Beep */}
                  <Group justify="space-between">
                    <div>
                      <Text size="sm">Status Change Beep</Text>
                      <Text size="xs" c="dimmed">active↔waiting, stuck, dead, etc.</Text>
                    </div>
                    <Switch
                      checked={globalConfig.beepEnabled}
                      onChange={(e) => updateGlobalConfig({ beepEnabled: e.currentTarget.checked })}
                    />
                  </Group>

                  {/* Playing Info */}
                  {isPlaying && poolSessions.filter(s => !mutedSessions.includes(s)).length > 0 && (
                    <Text size="xs" c="dimmed">
                      Playing for: {poolSessions.filter(s => !mutedSessions.includes(s)).join(', ')}
                    </Text>
                  )}

                  {/* Emergency Stop */}
                  <Button
                    color="red"
                    size="sm"
                    leftSection={<IconPlayerStop size={16} />}
                    onClick={emergencyStop}
                    fullWidth
                  >
                    Emergency Stop
                  </Button>
                </Stack>
              ) : (
                <Stack gap="md" align="center" py="md">
                  <IconLock size={32} color="gray" />
                  <Text size="sm" c="dimmed" ta="center">
                    Sign in to enable sound notifications
                  </Text>
                  <LoginButton />
                </Stack>
              )}
            </Popover.Dropdown>
          </Popover>

          {/* Connection Status */}
          {getConnectionBadge()}

          {/* Status Counts */}
          {statusCounts.waiting > 0 && (
            <Badge size="lg" variant="filled" color="yellow">
              {statusCounts.waiting} Waiting
            </Badge>
          )}
          {statusCounts.idle > 0 && (
            <Badge size="lg" variant="light" color="gray">
              {statusCounts.idle} Idle
            </Badge>
          )}
          {statusCounts.active > 0 && (
            <Badge size="lg" variant="light" color="blue">
              {statusCounts.active} Active
            </Badge>
          )}
          {statusCounts.stuck > 0 && (
            <Badge size="lg" variant="filled" color="red">
              {statusCounts.stuck} Stuck
            </Badge>
          )}
          {statusCounts.dead > 0 && (
            <Badge size="lg" variant="filled" color="dark">
              {statusCounts.dead} Dead
            </Badge>
          )}

          {/* Total Count */}
          <Badge size="lg" variant="light">
            {sessions.length} Total
          </Badge>
        </Group>
      </Group>

      {/* Monitored Sessions Grid */}
      <MonitoredGrid
        sessions={sessions}
        monitoredSessions={monitoredSessions}
        orderedMonitoredSessions={orderedMonitoredSessions}
        mutedSessions={mutedSessions}
        poolSessions={poolSessions}
        lastChangeTimeMap={lastChangeTimeMap}
        onReorder={reorder}
        onToggleMute={handleToggleMute}
        onUnmonitor={removeFromMonitored}
      />

      {/* All Sessions List (collapsible) */}
      <Box mt="xl">
      <SessionList
        sessions={sessions}
        monitoredSessions={monitoredSessions}
        onMonitor={addToMonitored}
      />
      </Box>

      {/* Footer Notice */}
      <Text c="dimmed" ta="center" size="xs" mt="xl" pt="md" style={{ borderTop: '1px solid var(--mantine-color-dark-4)' }}>
        If sound doesn't stop, press F5 to refresh
      </Text>
    </Container>
  )
}
